<script setup lang="ts">
    import { reactive, onMounted } from 'vue';
    import Tabs from '@shared/components/Tabs.vue';
    import Tab from '@shared/components/Tab.vue';
    import Loading from '@shared/components/Loading.vue';
    import FormInput from '@shared/components/Form/FormInput.vue';
    import FormCheckable from '@shared/components/Form/FormCheckable.vue';
    import InfoPopoverButton from '@shared/components/Form/InfoPopoverButton.vue';
    import { SwitchStyle } from '@shared/components/Form/Form';
    import { bsAlert, AlertTypes } from '@shared/components/bsAlerts';
    import { debounce } from '@shared/utils';
    import { Permissions } from '@shared/Root/Permissions';
    import { CurrentAccountReference } from '@shared/Root/Context.access';
    import AccountSettings from './AccountSettings/AccountSettings.vue';
    import DomainWhitelists from '@cwd/Account/DomainWhitelists/DomainWhitelists.vue';
    import { get } from './AccountInfo.access';
    import { updateCustomField } from './AccountGeneralSettings.access';

    interface CustomFieldEntry {
        key: string;
        isPublicKey: string;
        label: string;
        placeholder?: string;
        id: string;
    }

    const state = reactive<{
        isLoading: boolean;
        account: CurrentAccountReference | null;
        customFieldEntries: CustomFieldEntry[];
        customLongFieldEntries: CustomFieldEntry[];
        customDateFieldEntries: CustomFieldEntry[];
        customBoolFieldEntries: CustomFieldEntry[];
        customNumericFieldEntries: CustomFieldEntry[];
    }>({
        isLoading: true,
        account: null,
        customFieldEntries: [],
        customLongFieldEntries: [],
        customDateFieldEntries: [],
        customBoolFieldEntries: [],
        customNumericFieldEntries: [],
    });

    onMounted(async () => {
        state.account = await get();

        buildFieldEntries();

        state.isLoading = false;
    });

    function buildFieldEntries(): void {
        const resources = $Resources as unknown as Record<string, string>;

        state.customFieldEntries = Array.from({ length: 10 }, (_, i) => i + 1).map((n) => ({
            key: 'CustomField' + n,
            isPublicKey: 'CustomField' + n + 'IsPublic',
            label: (resources.CustomFieldLabel || '').replace('{Number}', String(n)),
            id: 'customField' + n,
        }));

        state.customLongFieldEntries = [1, 2].map((n) => ({
            key: 'CustomLong' + n,
            isPublicKey: 'CustomLong' + n + 'IsPublic',
            label: resources['ContactModalCustomLong' + n],
            placeholder: resources['ContactModalCustomLong' + n + 'Placeholder'],
            id: 'CustomLong' + n,
        }));

        state.customDateFieldEntries = [1, 2, 3, 4, 5, 6].map((n) => ({
            key: 'CustomDate' + n,
            isPublicKey: 'CustomDate' + n + 'IsPublic',
            label: resources['ContactModalCustomDate' + n],
            placeholder: resources['ContactModalCustomDate' + n + 'Placeholder'],
            id: 'CustomDate' + n,
        }));

        state.customBoolFieldEntries = [1, 2, 3, 4, 5, 6].map((n) => ({
            key: 'CustomBool' + n,
            isPublicKey: 'CustomBool' + n + 'IsPublic',
            label: resources['ContactModalCustomBool' + n],
            placeholder: resources['ContactModalCustomBool' + n + 'Placeholder'],
            id: 'CustomBool' + n,
        }));

        state.customNumericFieldEntries = [1, 2, 3, 4, 5, 6].map((n) => ({
            key: 'CustomNumeric' + n,
            isPublicKey: 'CustomNumeric' + n + 'IsPublic',
            label: (resources.CustomNumericLabel || '').replace('{Number}', String(n)),
            id: 'CustomNumeric' + n,
        }));
    }

    function fieldValue(entry: CustomFieldEntry): string {
        return (state.account!.CustomField as any)[entry.key] ?? '';
    }

    function isPublicValue(entry: CustomFieldEntry): boolean {
        return (state.account!.CustomField as any)[entry.isPublicKey] ?? false;
    }

    function updateFieldValue(entry: CustomFieldEntry, newValue: string): void {
        (state.account!.CustomField as any)[entry.key] = newValue;
        saveCustomFields();
    }

    function updateIsPublic(entry: CustomFieldEntry, newValue: boolean): void {
        (state.account!.CustomField as any)[entry.isPublicKey] = newValue;
        saveCustomFields();
    }

    function saveCustomFields(): void {
        debounce(
            () => {
                updateCustomField(state.account!.CustomField)
                    .then(() => {
                        bsAlert({
                            type: AlertTypes.Success,
                            title: $Resources.PropertyUpdatedLabel,
                            content: $Resources.PropertyAccountCustomFieldLabel,
                        });
                    })
                    .catch(() => {
                        bsAlert({
                            type: AlertTypes.Danger,
                            title: $Resources.PropertyNotUpdatedLabel,
                            content: $Resources.PropertyAccountCustomFieldLabel,
                        });
                    });
            },
            { delay: 800 },
            1
        );
    }
</script>

<template>
    <Loading :show="state.isLoading" loading-style="progress-bar" fixed></Loading>

    <div v-if="state.account">
        <h1 class="h2 m-lg-b">
            {{ $Resources.AccountTabSettings }}
            <small>({{ state.account.Name }})</small>
        </h1>

        <Tabs>
            <Tab :title="$Resources.AccountTabSettings">
                <AccountSettings></AccountSettings>
            </Tab>

            <Tab :title="$Resources.AccountPageTabsAccountCustomFields">
                <div v-require-scope="{ Feature: 'Account', Permission: Permissions.Read }">
                    <h2 class="h3">
                        {{ $Resources.AccountCustomFieldsHeading }}
                        <InfoPopoverButton
                            :heading="$Resources.AccountCustomFieldsHeading"
                            :info-text="$Resources.AccountCustomFieldsInfoText"
                            iconClasses="text-md"
                            direction="bottom" />
                    </h2>

                    <p
                        class="m-lg-b"
                        v-html="
                            $Resources.AccountPageTabsAccountTextWithLink.replace('{{PreviewPreferenceCenterUrl}}', state.account?.PreviewPreferenceCenterUrl)
                        "></p>

                    <h3 class="h4">{{ $Resources.CustomFieldsTextFieldsHeading }}</h3>

                    <div class="d-grid col-gap-lg cols-minmax-260-1fr">
                        <fieldset class="fieldset" v-for="entry in state.customFieldEntries" :key="entry.key">
                            <FormInput
                                class="form-group"
                                :id="entry.id"
                                :label="entry.label"
                                :maxlength="50"
                                :modelValue="fieldValue(entry)"
                                @update:modelValue="(newVal) => updateFieldValue(entry, newVal as string)"></FormInput>
                            <FormCheckable
                                class="form-group"
                                :label="$Resources.AccountCustomFieldsTogglePublicLabel"
                                :switch-style="SwitchStyle.Standard"
                                :checked="isPublicValue(entry)"
                                @update:modelValue="(newVal) => updateIsPublic(entry, newVal as boolean)"></FormCheckable>
                        </fieldset>
                    </div>

                    <h3 class="h4">{{ $Resources.CustomFieldsLongTextFieldsHeading }}</h3>

                    <div class="d-grid col-gap-lg cols-minmax-260-1fr">
                        <fieldset class="fieldset" v-for="entry in state.customLongFieldEntries" :key="entry.key">
                            <FormInput
                                class="form-group"
                                :id="entry.id"
                                :label="entry.label"
                                :placeholder="entry.placeholder"
                                :maxlength="50"
                                :modelValue="fieldValue(entry)"
                                @update:modelValue="(newVal) => updateFieldValue(entry, newVal as string)"></FormInput>
                            <FormCheckable
                                class="form-group"
                                :label="$Resources.AccountCustomFieldsTogglePublicLabel"
                                :switch-style="SwitchStyle.Standard"
                                :checked="isPublicValue(entry)"
                                @update:modelValue="(newVal) => updateIsPublic(entry, newVal as boolean)"></FormCheckable>
                        </fieldset>
                    </div>

                    <h3 class="h4">{{ $Resources.CustomFieldsDateFieldsHeading }}</h3>

                    <div class="d-grid col-gap-lg cols-minmax-260-1fr">
                        <fieldset class="fieldset" v-for="entry in state.customDateFieldEntries" :key="entry.key">
                            <FormInput
                                class="form-group"
                                :id="entry.id"
                                :label="entry.label"
                                :placeholder="entry.placeholder"
                                :maxlength="50"
                                :modelValue="fieldValue(entry)"
                                @update:modelValue="(newVal) => updateFieldValue(entry, newVal as string)"></FormInput>
                            <FormCheckable
                                class="form-group"
                                :label="$Resources.AccountCustomFieldsTogglePublicLabel"
                                :switch-style="SwitchStyle.Standard"
                                :checked="isPublicValue(entry)"
                                @update:modelValue="(newVal) => updateIsPublic(entry, newVal as boolean)"></FormCheckable>
                        </fieldset>
                    </div>

                    <h3 class="h4">{{ $Resources.CustomFieldsBooleanFieldsHeading }}</h3>

                    <div class="d-grid col-gap-lg cols-minmax-260-1fr">
                        <fieldset class="fieldset" v-for="entry in state.customBoolFieldEntries" :key="entry.key">
                            <FormInput
                                class="form-group"
                                :id="entry.id"
                                :label="entry.label"
                                :placeholder="entry.placeholder"
                                :maxlength="50"
                                :modelValue="fieldValue(entry)"
                                @update:modelValue="(newVal) => updateFieldValue(entry, newVal as string)"></FormInput>
                            <FormCheckable
                                class="form-group"
                                :label="$Resources.AccountCustomFieldsTogglePublicLabel"
                                :switch-style="SwitchStyle.Standard"
                                :checked="isPublicValue(entry)"
                                @update:modelValue="(newVal) => updateIsPublic(entry, newVal as boolean)"></FormCheckable>
                        </fieldset>
                    </div>

                    <h3 class="h4">{{ $Resources.CustomFieldsNumericFieldsHeading }}</h3>

                    <div class="d-grid col-gap-lg cols-minmax-260-1fr">
                        <fieldset class="fieldset" v-for="entry in state.customNumericFieldEntries" :key="entry.key">
                            <FormInput
                                class="form-group"
                                :id="entry.id"
                                :label="entry.label"
                                :maxlength="50"
                                :modelValue="fieldValue(entry)"
                                @update:modelValue="(newVal) => updateFieldValue(entry, newVal as string)"></FormInput>
                            <FormCheckable
                                class="form-group"
                                :label="$Resources.AccountCustomFieldsTogglePublicLabel"
                                :switch-style="SwitchStyle.Standard"
                                :checked="isPublicValue(entry)"
                                @update:modelValue="(newVal) => updateIsPublic(entry, newVal as boolean)"></FormCheckable>
                        </fieldset>
                    </div>
                </div>
            </Tab>

            <Tab :title="$Resources.DomainWhitelistsHeader">
                <div v-require-scope="{ Feature: 'Account', Permission: Permissions.Write }">
                    <DomainWhitelists></DomainWhitelists>
                </div>
            </Tab>
        </Tabs>
    </div>
</template>
